import { useEffect } from 'react';
import { timerToString, useTimer } from '../../core/util/date';
import './StatusTimer.css';
import { useAppContext, type OurContext } from '../../core/context';
import { Routes } from '../../Routes';
import formatOffScreenTime from '../../lib/formatters/format-off-screen-time';
import vippsSparkles from './sparkles_vipps.svg';
import mobilePaySparkles from './sparkles_mobilepay.svg';
import { Market } from '../../core/context/claims';
import { type LanguageContextProps, useLanguageContext } from '../../core/locale/LanguageContext';
import { useFlag } from '../../feature-flags';

interface Props {
  expiresAt: number;
  startTime: number;
}

const redirectOnTimeout = (timeLeft: number, context: OurContext) => {
  if (timeLeft <= 0) {
    context.setRoute(Routes.TIMEOUT);
  }
};

function computeOffscreenText(
  timeLeft: number,
  context: LanguageContextProps,
  updateIntervalInSeconds: number
) {
  // Every time the label text changes, the screen reader will read it out loud (i.e. every 20 seconds).
  const offScreenTime = formatOffScreenTime(timeLeft, updateIntervalInSeconds);
  const { minutesRemaining, secondsRemaining } = offScreenTime;
  return context.getLocalizedText('statusAriaLabel', minutesRemaining + '', secondsRemaining + '');
}

function StatusTimer({ expiresAt, startTime }: Props): React.ReactNode {
  const context = useAppContext();
  const languageContext = useLanguageContext();
  const showNewDesign = useFlag('redesign');

  const timeLeft = useTimer(expiresAt);
  const initialTotalTime = expiresAt - startTime;

  const rotation = Math.max(0, (timeLeft / initialTotalTime) * 360);
  useEffect(() => redirectOnTimeout(timeLeft, context), [timeLeft]);

  const hideIndicator = timeLeft <= 0 || timeLeft > 60 * 60;

  const sparkles = {
    [Market.DK]: mobilePaySparkles,
    [Market.FI]: mobilePaySparkles,
    [Market.NO]: vippsSparkles,
    [Market.SE]: vippsSparkles,
  }[context.claims!.market];

  if (showNewDesign) {
    if (hideIndicator) return;

    return (
      <>
        <progress
          id="status-progress"
          className="status-view-progress-bar"
          value={Math.floor(timeLeft)}
          max={Math.floor(initialTotalTime)}
          aria-labelledby="status-progress-label"
        />
        <div id="status-progress-label" className="offscreen">
          {computeOffscreenText(timeLeft, languageContext, 20)}
        </div>
      </>
    );
  }

  if (hideIndicator) {
    return <img src={sparkles} alt={'Logo'} />;
  }

  return (
    <div className="status-circle">
      <div className="offscreen">{computeOffscreenText(timeLeft, languageContext, 20)}</div>
      <div className="status-timer-text" aria-hidden>
        {timerToString(timeLeft)}
      </div>
      <div
        className={`status-circle-background ${rotation > 180 ? 'state-50-100' : 'state-0-50'}`}
      />
      <div className={`status-circle-50-100 ${rotation > 180 ? 'state-50-100' : 'state-0-50'}`} />
      <div
        className={`status-circle-spinner ${rotation > 180 ? 'state-50-100' : 'state-0-50'}`}
        style={{ rotate: `${180 + rotation}deg` }}
      />
      <div className="status-circle-ball-fixed" />
      <div className="status-circle-ball-moving" style={{ rotate: `${rotation + 90}deg` }} />
      <div className="status-circle-inner" />
    </div>
  );
}

export default StatusTimer;
